import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@/store/useUserStore';

interface CreateCustomerThreadPayload {
  id_customer?: number;
  id_order?: number;
  email: string;
  message: string;
}

interface CustomerThreadWithMessages {
  id: string;
  id_lang: string;
  id_shop: string;
  id_customer: string;
  id_order: string;
  id_contact: string;
  email: string;
  token: string;
  status: string;
  customer_messages?: Array<{
    id: string;
    id_customer_thread: string;
    message: string;
  }>;
}

export const useCustomerThreads = () => {
  const queryClient = useQueryClient();
  const { profile, customerId, isLogged } = useUserStore();

  // Crear customer thread con mensaje
  const createCustomerThreadMutation = useMutation({
    mutationFn: async (payload: CreateCustomerThreadPayload) => {
      const response = await fetch('/api/es/customer-threads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creando customer thread');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidar queries relacionadas después de crear
      queryClient.invalidateQueries({ queryKey: ['customerThreads'] });
    },
  });

  // Obtener customer threads por customer ID
  const getCustomerThreads = useQuery({
    queryKey: ['customerThreads', customerId],
    queryFn: async (): Promise<CustomerThreadWithMessages[]> => {
      if (!customerId) {
        return [];
      }

      const response = await fetch(`/api/es/customer-threads?customerId=${customerId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error obteniendo customer threads');
      }

      return response.json();
    },
    enabled: !!customerId, // Solo ejecutar si hay un customer ID
  });

  // Función helper para crear thread con datos del usuario actual
  const createThreadWithCurrentUser = (message: string, email?: string) => {
    const payload: CreateCustomerThreadPayload = {
      id_customer: customerId || undefined,
      id_order: undefined, // Como especificaste, la orden se envía como undefined
      email: email || profile?.email || '',
      message,
    };

    return createCustomerThreadMutation.mutateAsync(payload);
  };

  return {
    // Mutations
    createCustomerThread: createCustomerThreadMutation,
    createThreadWithCurrentUser,
    
    // Queries
    getCustomerThreads,
    
    // Estados
    isLoading: createCustomerThreadMutation.isPending,
    isError: createCustomerThreadMutation.isError,
    error: createCustomerThreadMutation.error,
    
    // Estados de queries
    isQueryLoading: getCustomerThreads.isLoading,
    isQueryError: getCustomerThreads.isError,
    queryError: getCustomerThreads.error,
    customerThreads: getCustomerThreads.data || [],
    
    // User info
    isLogged,
    userEmail: profile?.email,
  };
};
